import type { BudgetVarianceRow } from '@/components/financials/shared/types'
import { budgetComparisonTotals } from '@/components/financials/shared/types'
import { projectBudgetPlan3Api } from '@/lib/api/projectBudget.api'

import type {
  BudgetVarianceInsights,
  BudgetVarianceMode,
  BudgetVarianceTotals,
} from '@scrambl/shared'

import { useFinancialReadQuery } from './useFinancialReadQuery'

interface Params {
  /** Date-only strings; both or neither. Bounds the row actuals and totals. */
  from?: string
  to?: string
  entityIds?: string[]
  projectIds?: string[]
  /**
   * With no explicit scope, roll up the whole org instead of nothing.
   * Opt-in: single-record consumers (e.g. project Profitability) must not
   * fan out org-wide while their id is still loading.
   */
  orgWideWhenUnscoped?: boolean
  /**
   * Which level to roll up. Defaults to project budgets; the Financials
   * overview passes the viewer's persisted lens (useBudgetLensPreference).
   */
  mode?: BudgetVarianceMode
  /**
   * Ask for the per-row agent split and the leverage insight (agent labor
   * ledger). Only pass true when useAgentLedgerEnabled says so.
   */
  includeAgentSplit?: boolean
}

export interface Rollup {
  mode: BudgetVarianceMode
  /** Sum of the rows' budget / forecast / lifetime budget-side actuals. */
  totals: {
    budget: number | null
    forecast: number | null
    actual: number | null
  }
  budgetBasis: BudgetVarianceRow['budgetBasis']
  coverage: { withBudget: number; total: number }
  rows: BudgetVarianceRow[]
  /** Period revenue and cost as the API summed them (cost null when masked). */
  periodTotals: BudgetVarianceTotals | null
  /** Same for the window before the period (whole months when the period is whole months); null without one. */
  priorTotals: BudgetVarianceTotals | null
  /** Same for the same calendar window one year earlier; null without a period. */
  priorYearTotals: BudgetVarianceTotals | null
  insights: BudgetVarianceInsights | null
  isLoading: boolean
  error?: unknown
  refetch: () => unknown
}

export const financialsRollupKeys = {
  all: ['financials-rollup'] as const,
  rollup: (
    mode: BudgetVarianceMode,
    entityIds: string[],
    projectIds: string[],
    from?: string,
    to?: string,
    includeAgentSplit = false,
  ) =>
    [
      ...financialsRollupKeys.all,
      mode,
      [...entityIds].sort().join(','),
      [...projectIds].sort().join(','),
      from ?? '',
      to ?? '',
      includeAgentSplit ? 'agentSplit' : '',
    ] as const,
}

/**
 * Budget variance on each declared basis for every project (or client), from ONE
 * request. The API computes each row with the same rules as the per-record
 * budget summaries (mirrored side, DRAFT exclusion, work/expense
 * inclusion, cost masking), so this stays in step with the Budget tab
 * without fetching a summary per row. With a period it also carries the
 * prior-period totals and the attention counts the overview leads with.
 *
 * Budget writes invalidate `financialsRollupKeys.all` alongside the summary
 * caches so the table refreshes with them.
 */
export function useFinancialsRollup(params: Params = {}): Rollup {
  const {
    entityIds = [],
    projectIds = [],
    orgWideWhenUnscoped = false,
    mode = 'PROJECT',
    includeAgentSplit = false,
  } = params
  const hasPeriod = Boolean(params.from && params.to)
  const from = hasPeriod ? params.from : undefined
  const to = hasPeriod ? params.to : undefined

  const hasScope = entityIds.length > 0 || projectIds.length > 0
  const enabled = hasScope || orgWideWhenUnscoped

  const query = useFinancialReadQuery({
    queryKey: financialsRollupKeys.rollup(
      mode,
      entityIds,
      projectIds,
      from,
      to,
      includeAgentSplit,
    ),
    queryFn: signal =>
      projectBudgetPlan3Api.getVarianceRollup(
        {
          mode,
          entityIds,
          projectIds,
          from,
          to,
          ...(includeAgentSplit ? { includeAgentSplit: true } : {}),
        },
        signal,
      ),
    enabled,
    staleTime: 60_000,
  })

  const rows: BudgetVarianceRow[] = (query.data?.rows ?? []).map(row => ({
    id: row.id,
    name: row.name,
    groupId: row.groupId,
    groupName: row.groupName,
    actualRevenue: row.actualRevenue,
    budgetRevenue: row.budgetRevenue,
    forecastRevenue: row.forecastRevenue,
    actualCost: row.actualCost,
    hasPeriodActivity: row.hasPeriodActivity,
    toDateRevenue: row.toDate ? row.toDate.revenue : row.actualRevenue,
    toDateCost: row.toDate ? row.toDate.cost : row.actualCost,
    budgetStatus: row.budgetStatus,
    budgetBasis: row.budgetBasis,
    budgetActual: row.budgetActual,
    ...(row.agent ? { agent: row.agent } : {}),
  }))

  const totals = budgetComparisonTotals(rows)
  const withBudget = query.data?.withBudget ?? 0

  return {
    mode,
    totals: {
      budget: totals.budget,
      forecast: totals.forecast,
      actual: query.data ? totals.actual : null,
    },
    budgetBasis: totals.basis,
    coverage: { withBudget, total: rows.length },
    rows,
    periodTotals: query.data?.totals ?? null,
    priorTotals: query.data?.prior ?? null,
    priorYearTotals: query.data?.priorYear ?? null,
    insights: query.data?.insights ?? null,
    // isPending: a query waiting on the persisted cache is not fetching yet,
    // and isLoading would show the empty hint before it ran.
    isLoading: enabled && query.isPending,
    error: query.error ?? undefined,
    refetch: query.refetch,
  }
}
